import React, { useMemo, useState } from 'react';
import styles from '../../../styles/calendar.module.css';
import { requesturl } from '../../../shared/utils/requesturl';

const EVENTS_API = `${requesturl}events.php`;

// Utility: format Date to YYYY-MM-DD (local timezone, no UTC conversion)
const fmt = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function fmtShort(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) {
    return d;
  }
}

function fmtShortWithTime(d) {
  try {
    const date = new Date(d);
    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const hours = date.getHours();
    const mins = date.getMinutes();
    if (hours === 0 && mins === 0) return dateStr;
    const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  } catch (e) {
    return d;
  }
}

function addDays(d, amount) {
  const x = new Date(d);
  x.setDate(x.getDate() + amount);
  return x;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = endOfMonth(first);
  const startWeekDay = first.getDay(); // 0 (Sun) - 6

  // Backfill days from previous month to align week start (Sunday)
  const startDate = addDays(first, -startWeekDay);

  const grid = [];
  // 6 weeks x 7 days = 42 cells (covers any month)
  for (let i = 0; i < 42; i++) {
    const d = addDays(startDate, i);
    grid.push(d);
  }
  return grid;
}

// events: [{ id, title, start: 'YYYY-MM-DD', end: 'YYYY-MM-DD', color?: '#hex' }]
export default function Calendar({ events, initialDate = new Date() }) {
  // Default fallback events (used if fetch fails and no `events` prop provided)
  const defaultEvents = [
    { id: 1, title: 'Science Week', start: '2025-12-08', end: '2025-12-12', color: '#f97316' },
    { id: 2, title: 'Winter Break', start: '2025-12-24', end: '2026-01-04', color: '#10b981' },
    { id: 4, title: 'Math Olympiad', start: '2025-12-10T14:30:00', end: '2025-12-10T16:00:00', color: '#6366f1' },
  ];

  const [remoteEvents, setRemoteEvents] = useState(null);

  const [current, setCurrent] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD

  // Map dates to event info for quick lookup
  const dateEventMap = useMemo(() => {
    const map = new Map();
    const effectiveEvents = (Array.isArray(events) && events.length) ? events : (Array.isArray(remoteEvents) && remoteEvents.length ? remoteEvents : defaultEvents);
    for (const ev of effectiveEvents) {
      const s = new Date(ev.start);
      const e = ev.end ? new Date(ev.end) : new Date(ev.start);
      for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
        const key = fmt(d);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ ...ev, isStart: fmt(d) === ev.start, isEnd: fmt(d) === (ev.end || ev.start) });
      }
    }
    return map;
  }, [events, remoteEvents]);

  const grid = useMemo(() => buildMonthGrid(current.getFullYear(), current.getMonth()), [current]);

  // Fetch events from backend on mount when no `events` prop supplied
  React.useEffect(() => {
    if (Array.isArray(events) && events.length) return; // using prop events

    let cancelled = false;
    fetch(EVENTS_API)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setRemoteEvents(data);
          console.log(remoteEvents)
        }
      })
      .catch((e) => {
        // ignore errors — we'll use defaultEvents
        
      });

    return () => { cancelled = true; };
  }, [events]);

  const liveMessage = useMemo(() => {
    if (!selectedDate) return '';
    const evs = dateEventMap.get(selectedDate) || [];
    if (!evs.length) return `No events on ${selectedDate}.`;
    const parts = evs.map(e => `${e.title} (${fmtShortWithTime(e.start)} → ${fmtShortWithTime(e.end)})`);
    return `Events on ${selectedDate}: ${parts.join('; ')}.`;
  }, [selectedDate, dateEventMap]);

  function prevMonth() {
    setCurrent((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }

  function nextMonth() {
    setCurrent((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }

  const monthLabel = current.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={styles.calendar}>
      <div aria-live="polite" role="status" style={{position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden'}}>
        {liveMessage}
      </div>
      <div className={styles.header}>
        <button className={styles.nav} onClick={prevMonth} aria-label="Previous month">‹</button>
        <div className={styles.monthLabel}>{monthLabel}</div>
        <button className={styles.nav} onClick={nextMonth} aria-label="Next month">›</button>
      </div>

      {selectedDate && (
        <div className={styles.liveStatus} aria-hidden>
          {liveMessage}
        </div>
      )}

      <div className={styles.weekdays}>
        {weekdays.map((w) => (
          <div key={w} className={styles.weekday}>{w}</div>
        ))}
      </div>

      <div className={styles.grid}>
        {grid.map((d) => {
          const key = fmt(d);
          const inCurrentMonth = d.getMonth() === current.getMonth();
          const eventsHere = dateEventMap.get(key) || [];

          const classes = [styles.dayCell];
          if (!inCurrentMonth) classes.push(styles.outside);
          if (eventsHere.length) classes.push(styles.hasEvent);

          return (
            <div
              key={key}
              className={classes.join(' ')}
              title={eventsHere.map(e => `${e.title} ${e.isStart ? '(start)' : e.isEnd ? '(end)' : ''}`).join('\n')}
              onClick={() => setSelectedDate(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') setSelectedDate(key); }}
            >
              <div className={styles.dateNum}>{d.getDate()}</div>
              <div className={styles.eventDots}>
                {eventsHere.map((e) => (
                  <span
                    key={e.id}
                    className={
                      e.isStart ? styles.eventStartDot : e.isEnd ? styles.eventEndDot : styles.eventMidDot
                    }
                    style={{ backgroundColor: e.color || '#3b82f6' }}
                    aria-hidden
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selectedDate && (
        <div className={styles.eventPanel} role="dialog" aria-modal="true">
          <div className={styles.panelHeader}>
            <div>Events on {selectedDate}</div>
            <button className={styles.closeBtn} onClick={() => setSelectedDate(null)} aria-label="Close">✕</button>
          </div>
          <div className={styles.panelBody}>
            {(dateEventMap.get(selectedDate) || []).map((ev) => (
              <div key={ev.id} className={styles.eventItem}>
                <div className={styles.eventTitle} style={{ color: ev.color || '#111' }}>{ev.title}</div>
                <div className={styles.eventRange}>{fmtShortWithTime(ev.start)} → {fmtShortWithTime(ev.end || ev.start)}</div>
              </div>
            ))}
            {(!(dateEventMap.get(selectedDate) || []).length) && (
              <div className={styles.eventItem}>No events</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
